import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Play, RotateCcw, SkipForward, Check, CheckCircle2, Sparkles, Camera,
  ArrowRight, Info,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Section } from './blocks';
import {
  DEMO_STEPS, INTELLIGENCE_PILLARS, MOCK_TEXTBOOK_PAGE,
} from './experienceBoscoCaptureScript';

/**
 * ExperienceBoscoCaptureDemo — interactive landing-page demonstration.
 *
 * Purpose (per product brief):
 *   Communicate BOSCO's educational intelligence, not OCR. Visitors leave
 *   the demo with full clarity on the workflow: read → understand →
 *   validate → generate → done, without signing up or touching a camera.
 *
 * Architecture notes:
 *   • Presentation logic ONLY. The script data lives in
 *     `experienceBoscoCaptureScript.js` so a future real BOSCO Capture
 *     session can be plugged in without any JSX changes.
 *   • Step machine is driven by a reducer + one active setTimeout chain.
 *     Any user action (Skip, Restart, unmount) cancels the chain via a
 *     `tokenRef` guard — no orphan timers.
 *   • Respects `prefers-reduced-motion` — halves durations and disables
 *     the CSS scan-line animation.
 *   • ARIA: the caption panel is an aria-live=polite region so screen
 *     readers announce every status update as it appears.
 */

// Reducer action types
const ACT = {
  START:  'start',
  NEXT:   'next',       // move to the next step
  TICK:   'tick',       // reveal the next item within the current step
  SKIP:   'skip',       // jump to the final step
  RESET:  'reset',
  KIT_TOGGLE: 'kit_toggle',
};

const initialState = {
  started: false,        // has the user pressed ▶
  stepIndex: 0,          // index into DEMO_STEPS
  revealed: 0,           // how many items of the current step have appeared
  kitSelections: {},     // {itemId: bool} — user overrides on step 4
  ready: {},             // {label: true} — resources marked "ready" on step 6
};

function reducer(state, action) {
  switch (action.type) {
    case ACT.START:
      return { ...initialState, started: true, kitSelections: seedKitSelections() };
    case ACT.NEXT: {
      const next = Math.min(state.stepIndex + 1, DEMO_STEPS.length - 1);
      return { ...state, stepIndex: next, revealed: 0, ready: next === 4 ? {} : state.ready };
    }
    case ACT.TICK: {
      const step = DEMO_STEPS[state.stepIndex];
      const cap = (step.items || []).length;
      return { ...state, revealed: Math.min(state.revealed + 1, cap) };
    }
    case ACT.SKIP:
      return {
        ...state,
        stepIndex: DEMO_STEPS.length - 1,
        revealed: DEMO_STEPS[DEMO_STEPS.length - 1].items.length,
        ready: Object.fromEntries(DEMO_STEPS[4].items.map((i) => [i.label, true])),
      };
    case ACT.RESET:
      return initialState;
    case ACT.KIT_TOGGLE:
      return {
        ...state,
        kitSelections: {
          ...state.kitSelections,
          [action.id]: !state.kitSelections[action.id],
        },
      };
    default:
      return state;
  }
}

function seedKitSelections() {
  const out = {};
  for (const it of DEMO_STEPS[3].items) out[it.id] = !!it.checked;
  return out;
}

// ─────────────────────────────────────────────────────────────────────
export default function ExperienceBoscoCaptureDemo() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [reducedMotion, setReducedMotion] = useState(false);
  const tokenRef = useRef(0);     // increment to invalidate any scheduled step timers
  const captionRef = useRef(null);

  // Honour user OS preference on load + updates
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReducedMotion(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);

  // Cancel any in-flight timers when the widget unmounts.
  useEffect(() => () => { tokenRef.current += 1; }, []);

  // ── Step timer engine ──────────────────────────────────────────────
  // For each step: reveal items one-by-one across the step's duration,
  // then dispatch NEXT. Uses `tokenRef` to guard against races.
  useEffect(() => {
    if (!state.started) return undefined;
    const step = DEMO_STEPS[state.stepIndex];
    if (!step || step.duration === 0) return undefined;

    tokenRef.current += 1;
    const myToken = tokenRef.current;

    const items = step.items || [];
    const speedFactor = reducedMotion ? 0.5 : 1;
    const perItem = items.length ? (step.duration / items.length) * speedFactor : step.duration * speedFactor;

    let cancelled = false;
    const timers = [];

    // Reveal items on a rolling clock.
    for (let i = 0; i < items.length; i += 1) {
      const t = setTimeout(() => {
        if (cancelled || tokenRef.current !== myToken) return;
        dispatch({ type: ACT.TICK });
      }, perItem * (i + 1));
      timers.push(t);
    }

    // After all items are revealed, hold for a short beat then advance.
    const advanceIn = (items.length ? perItem * items.length : perItem) + (reducedMotion ? 500 : 900);
    const nextT = setTimeout(() => {
      if (cancelled || tokenRef.current !== myToken) return;
      dispatch({ type: ACT.NEXT });
    }, advanceIn);
    timers.push(nextT);

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [state.started, state.stepIndex, reducedMotion]);

  // Move focus to the caption region on each step change so screen-reader
  // users track the demo without needing to hunt.
  useEffect(() => {
    if (!state.started) return;
    if (!captionRef.current) return;
    // Only nudge focus once per step, not per item reveal.
    if (state.revealed === 0) captionRef.current.setAttribute('aria-busy', 'true');
    else captionRef.current.setAttribute('aria-busy', 'false');
  }, [state.stepIndex, state.revealed, state.started]);

  const step   = DEMO_STEPS[state.stepIndex];
  const isDone = step?.phase === 'done';

  const start   = useCallback(() => dispatch({ type: ACT.START }), []);
  const reset   = useCallback(() => { tokenRef.current += 1; dispatch({ type: ACT.RESET }); }, []);
  const skipToEnd = useCallback(() => { tokenRef.current += 1; dispatch({ type: ACT.SKIP }); }, []);

  return (
    <Section
      id="experience-bosco-capture"
      tone="default"
      className="relative overflow-hidden"
    >
      <SectionHeader />

      <div
        className="mt-8 grid grid-cols-1 lg:grid-cols-[minmax(0,340px)_1fr] gap-8 lg:gap-10 items-start"
        data-testid="experience-bosco-widget"
        role="region"
        aria-label="Interactive BOSCO Capture demonstration"
      >
        {/* ── LEFT — Phone frame with the mock textbook page ── */}
        <PhoneFrame
          phase={step?.phase || 'idle'}
          started={state.started}
          reducedMotion={reducedMotion}
        />

        {/* ── RIGHT — Caption panel that morphs by step ── */}
        <div
          ref={captionRef}
          aria-live="polite"
          aria-atomic="false"
          className="min-h-[380px]"
        >
          {!state.started ? (
            <IdleCard onStart={start} />
          ) : (
            <>
              <StepHeader step={step} stepIndex={state.stepIndex} total={DEMO_STEPS.length + 1} />

              <div className="mt-5">
                {step.phase === 'scan'       && <StatusList items={step.items} revealed={state.revealed} tone="scan" />}
                {step.phase === 'understand' && <StatusList items={step.items} revealed={state.revealed} tone="understand" />}
                {step.phase === 'review'     && <ReviewChecklist items={step.items} revealed={state.revealed} />}
                {step.phase === 'kit'        && (
                  <KitPickerMock
                    items={step.items}
                    selections={state.kitSelections}
                    onToggle={(id) => dispatch({ type: ACT.KIT_TOGGLE, id })}
                  />
                )}
                {step.phase === 'generate'   && <GenerateList items={step.items} revealed={state.revealed} finaleLabel={step.finaleLabel} />}
                {isDone                      && <DoneCard />}
              </div>

              {/* Progress rail + controls */}
              <div className="mt-6 flex items-center gap-3">
                <ProgressRail current={state.stepIndex + 1} total={DEMO_STEPS.length + 1} />
                {!isDone && (
                  <button
                    type="button"
                    onClick={skipToEnd}
                    className="text-[11px] font-medium text-slate-500 hover:text-slate-800 dark:hover:text-slate-100 inline-flex items-center gap-1"
                    data-testid="experience-bosco-skip"
                    aria-label="Skip to end of demo"
                  >
                    <SkipForward className="h-3.5 w-3.5" /> Skip
                  </button>
                )}
                <button
                  type="button"
                  onClick={reset}
                  className="text-[11px] font-medium text-slate-500 hover:text-slate-800 dark:hover:text-slate-100 inline-flex items-center gap-1"
                  data-testid="experience-bosco-restart"
                  aria-label="Restart demo"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Restart
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Intelligence rail — always on, reinforces the "understanding" story */}
      <IntelligenceRail />

      {/* Disclaimer */}
      <p className="mt-6 text-[11px] text-slate-500 dark:text-slate-500 text-center flex items-center justify-center gap-1.5"
         data-testid="experience-bosco-disclaimer">
        <Info className="h-3 w-3" />
        Demonstration uses a simulated textbook page to illustrate the BOSCO Capture workflow.
      </p>
    </Section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Section chrome
// ─────────────────────────────────────────────────────────────────────
function SectionHeader() {
  return (
    <div className="flex flex-col items-center text-center">
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-mk-ice dark:bg-mk-midnight/40 text-mk-deep dark:text-mk-cyan text-[10px] font-bold uppercase tracking-[0.22em]"
        data-testid="experience-bosco-badge"
      >
        <Sparkles className="h-3 w-3" /> Interactive Demo
      </span>
      <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-mk-ink dark:text-slate-50"
          data-testid="experience-bosco-title">
        Experience BOSCO Capture
      </h2>
      <p className="mt-3 text-base text-mk-muted dark:text-slate-300 max-w-2xl">
        See how BOSCO transforms a textbook page into complete classroom-ready teaching resources in under a minute.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Left column — the phone frame
// ─────────────────────────────────────────────────────────────────────
function PhoneFrame({ phase, started, reducedMotion }) {
  const showScan = phase === 'scan';
  return (
    <div className="mx-auto lg:mx-0" data-testid="experience-bosco-phone">
      <div className="relative aspect-[9/16] max-w-[280px] rounded-[2.2rem] bg-gradient-to-br from-slate-700 to-slate-950 p-1.5 shadow-2xl shadow-black/40">
        {/* Phone bezel */}
        <div className="relative h-full w-full rounded-[1.9rem] bg-mk-midnight overflow-hidden">
          {/* Status bar */}
          <div className="absolute top-0 inset-x-0 flex justify-between items-center px-6 pt-2 text-[10px] text-slate-300 font-medium z-20">
            <span>9:41</span>
            <span className="inline-flex items-center gap-1">
              <Camera className="h-3 w-3" /> BOSCO
            </span>
          </div>

          {/* Camera viewfinder brackets — visible in scan phase only */}
          {(started && (phase === 'scan')) && (
            <div className="absolute inset-6 top-8 rounded-2xl border-2 border-mk-cyan/50 pointer-events-none z-10">
              <BracketCorner pos="top-0 left-0"     dir="tl" />
              <BracketCorner pos="top-0 right-0"    dir="tr" />
              <BracketCorner pos="bottom-0 left-0"  dir="bl" />
              <BracketCorner pos="bottom-0 right-0" dir="br" />
            </div>
          )}

          {/* Textbook page */}
          <div className="absolute inset-6 top-8 bottom-14 rounded-xl bg-[#faf6ec] overflow-hidden">
            <TextbookPageSVG />
            {/* Scan line — CSS-only; disabled under prefers-reduced-motion */}
            {showScan && !reducedMotion && (
              <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-transparent via-mk-cyan to-transparent shadow-[0_0_16px_2px_rgba(52,211,153,0.7)] animate-[expScan_2.2s_ease-in-out_infinite]" />
            )}
            {/* Understanding overlay — subtle emerald tint */}
            {phase === 'understand' && (
              <div className="absolute inset-0 bg-gradient-to-b from-mk-blue/15 via-transparent to-mk-blue/15" />
            )}
            {/* Kit/generate overlay — page dims, chip flies */}
            {(phase === 'kit' || phase === 'generate' || phase === 'done') && (
              <div className="absolute inset-0 bg-mk-midnight/25" />
            )}
          </div>

          {/* Bottom hint / capture chip */}
          <div className="absolute bottom-0 inset-x-0 flex flex-col items-center px-4 pb-3 z-10">
            <div className="text-[10px] text-mk-cyan/80 mb-1 min-h-[14px] transition-opacity">
              {!started && 'Tap Experience to begin'}
              {started && phase === 'scan' && 'Hold steady · Analysing…'}
              {started && phase === 'understand' && 'Understanding chapter content…'}
              {started && phase === 'review' && 'Awaiting your review'}
              {started && phase === 'kit' && 'Select your teaching kit'}
              {started && phase === 'generate' && 'Generating resources…'}
              {started && phase === 'done' && 'Kit ready'}
            </div>
            <div className={`h-9 w-9 rounded-full border-2 ${phase === 'scan' ? 'border-mk-cyan bg-mk-blue/30' : 'border-slate-500'}`} />
          </div>
        </div>
      </div>

      {/* Keyframes local to the widget — kept inline to avoid touching index.css */}
      <style>{`
        @keyframes expScan {
          0%   { transform: translateY(0);    opacity: 0; }
          10%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translateY(240px); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

function BracketCorner({ pos, dir }) {
  const cls = {
    tl: 'border-t-2 border-l-2 rounded-tl-md',
    tr: 'border-t-2 border-r-2 rounded-tr-md',
    bl: 'border-b-2 border-l-2 rounded-bl-md',
    br: 'border-b-2 border-r-2 rounded-br-md',
  }[dir];
  return <span className={`absolute ${pos} h-3.5 w-3.5 border-mk-cyan ${cls} -translate-x-[1px] -translate-y-[1px]`} />;
}

// Mock textbook page — a lightweight SVG so we look genuinely like a
// textbook without shipping a heavy image asset.
function TextbookPageSVG() {
  const p = MOCK_TEXTBOOK_PAGE;
  return (
    <svg viewBox="0 0 200 320" className="h-full w-full text-slate-800" role="img" aria-label="Simulated textbook page: Chapter 6, Photosynthesis">
      <rect x="0" y="0" width="200" height="320" fill="#faf6ec" />
      {/* Chapter header */}
      <text x="12" y="18" fontFamily="Georgia, serif" fontSize="7" fill="#64748b" fontWeight="600" letterSpacing="1.2">{p.header.toUpperCase()}</text>
      <text x="12" y="34" fontFamily="Georgia, serif" fontSize="14" fill="#0f172a" fontWeight="700">{p.title}</text>
      <line x1="12" y1="40" x2="72" y2="40" stroke="#059669" strokeWidth="1.4" />
      {/* Intro paragraph — 3 text lines */}
      <TextLines x={12} y={52} width={176} lines={3} />
      {/* Subheading */}
      <text x="12" y="94" fontFamily="Georgia, serif" fontSize="9" fill="#0f172a" fontWeight="700">{p.subheading}</text>
      <TextLines x={12} y={104} width={176} lines={4} />
      {/* Equation box */}
      <rect x="20" y="150" width="160" height="18" fill="#e5f3ec" stroke="#059669" strokeOpacity="0.4" rx="2" />
      <text x="100" y="163" textAnchor="middle" fontFamily="Georgia, serif" fontSize="8" fill="#065f46">{p.equation}</text>
      {/* Diagram placeholder — cross-section of a leaf (very schematic) */}
      <g transform="translate(12, 176)">
        <rect x="0" y="0" width="80" height="46" fill="#ffffff" stroke="#94a3b8" strokeDasharray="2 2" />
        <path d="M6 30 Q40 4 74 30 Q40 42 6 30 Z" fill="#a7f3d0" stroke="#059669" strokeWidth="0.6" />
        <text x="40" y="42" textAnchor="middle" fontSize="4.5" fill="#334155">Fig 6.1 · Leaf cross-section</text>
      </g>
      {/* Table snippet */}
      <g transform="translate(100, 176)">
        <rect x="0" y="0" width="88" height="46" fill="#ffffff" stroke="#cbd5e1" />
        <line x1="0" y1="12" x2="88" y2="12" stroke="#cbd5e1" />
        <line x1="0" y1="26" x2="88" y2="26" stroke="#cbd5e1" />
        <line x1="0" y1="40" x2="88" y2="40" stroke="#cbd5e1" />
        <line x1="44" y1="0" x2="44" y2="46" stroke="#cbd5e1" />
        <text x="8" y="9" fontSize="4.5" fill="#0f172a" fontWeight="600">Factor</text>
        <text x="52" y="9" fontSize="4.5" fill="#0f172a" fontWeight="600">Effect</text>
        <text x="8" y="22" fontSize="4.2" fill="#334155">Light</text>
        <text x="52" y="22" fontSize="4.2" fill="#334155">Increases</text>
        <text x="8" y="36" fontSize="4.2" fill="#334155">CO₂</text>
        <text x="52" y="36" fontSize="4.2" fill="#334155">Increases</text>
      </g>
      {/* Exercises */}
      <text x="12" y="242" fontFamily="Georgia, serif" fontSize="8" fill="#0f172a" fontWeight="700">{p.exercisesLabel}</text>
      <line x1="12" y1="246" x2="52" y2="246" stroke="#059669" strokeWidth="1" />
      {p.exercises.map((_, i) => (
        <g key={i} transform={`translate(12, ${254 + i * 16})`}>
          <text x="0" y="8" fontSize="5.5" fill="#475569" fontWeight="600">{i + 1}.</text>
          <TextLines x={8} y={2} width={168} lines={1} lineHeight={6} />
        </g>
      ))}
      {/* Page number */}
      <text x="100" y="312" textAnchor="middle" fontSize="5" fill="#94a3b8">— 42 —</text>
    </svg>
  );
}

// Cheap "gray bar" text lines so the page looks read-dense without shipping
// real Lorem-ipsum copy that a screen reader would try to speak.
function TextLines({ x, y, width, lines, lineHeight = 8 }) {
  return (
    <g transform={`translate(${x}, ${y})`} aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <rect
          key={i}
          x={0}
          y={i * lineHeight}
          width={i === lines - 1 ? width * 0.72 : width}
          height={2.2}
          fill="#94a3b8"
          opacity="0.45"
          rx="1"
        />
      ))}
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Right column — caption cards per step
// ─────────────────────────────────────────────────────────────────────
function IdleCard({ onStart }) {
  return (
    <div className="rounded-2xl border border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy p-6 sm:p-8 shadow-sm">
      <h3 className="text-lg font-semibold text-mk-ink dark:text-slate-50">
        Ready when you are.
      </h3>
      <p className="mt-2 text-sm text-mk-muted dark:text-slate-400 leading-relaxed">
        This is a guided walkthrough of the BOSCO Capture workflow.
        You&rsquo;ll see how BOSCO reads a textbook page, understands its
        educational structure, hands control back to you for review, and
        finally assembles a full teaching kit.
      </p>
      <Button
        onClick={onStart}
        size="lg"
        className="mt-5 bg-mk-blue hover:bg-mk-deep text-white font-semibold shadow shadow-mk-blue/30 h-11"
        data-testid="experience-bosco-start"
      >
        <Play className="h-4 w-4 mr-1.5" /> Experience BOSCO Capture
      </Button>
      <p className="mt-3 text-[11px] text-slate-500">
        No signup · No camera · No AI usage
      </p>
    </div>
  );
}

function StepHeader({ step, stepIndex, total }) {
  // stepIndex is 0..(DEMO_STEPS.length-1) but spec numbers the idle
  // preview as step 1 — so the first animated step reads "Step 2 of 7".
  return (
    <div>
      <div className="flex items-center gap-2 text-mk-deep dark:text-mk-cyan text-[10px] font-bold uppercase tracking-[0.2em]">
        Step {stepIndex + 2} of {total}
        <span className="text-slate-400 dark:text-slate-500 font-normal normal-case tracking-normal">· {step.phase}</span>
      </div>
      <h3 className="mt-1.5 text-xl sm:text-2xl font-bold tracking-tight text-mk-ink dark:text-slate-50">
        {step.title}
      </h3>
      {step.subtitle && (
        <p className="mt-1 text-sm text-mk-muted dark:text-slate-400 leading-relaxed">{step.subtitle}</p>
      )}
    </div>
  );
}

function StatusList({ items, revealed, tone }) {
  const dotClass = tone === 'understand'
    ? 'bg-mk-blue'
    : 'bg-mk-blue';
  return (
    <ul className="space-y-2" data-testid={`experience-bosco-status-${tone}`}>
      {items.map((it, i) => {
        const shown = i < revealed;
        return (
          <li
            key={it.label}
            className={`flex items-center gap-2 text-sm transition-all duration-300
              ${shown ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-1'}`}
          >
            <span className={`h-4 w-4 rounded-full ${dotClass} text-white flex items-center justify-center flex-shrink-0`}>
              <Check className="h-2.5 w-2.5" strokeWidth={3.5} />
            </span>
            <span className="text-slate-800 dark:text-slate-100">{it.label}</span>
          </li>
        );
      })}
    </ul>
  );
}

function ReviewChecklist({ items, revealed }) {
  return (
    <div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="experience-bosco-review">
        {items.map((it, i) => {
          const shown = i < revealed;
          return (
            <li
              key={it.label}
              className={`flex items-start gap-2 rounded-lg border border-mk-line/70 dark:border-mk-navy/50 bg-mk-ice/50 dark:bg-mk-midnight/20 px-2.5 py-2 transition-all duration-300
                ${shown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}`}
            >
              <CheckCircle2 className="h-4 w-4 text-mk-blue dark:text-mk-cyan flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-xs font-semibold text-mk-ink dark:text-slate-100">{it.label}</div>
                {it.detail && <div className="text-[11px] text-mk-muted dark:text-slate-400 truncate">{it.detail}</div>}
              </div>
            </li>
          );
        })}
      </ul>
      <p className="mt-3 text-[11px] text-slate-500 italic">
        You remain in control. Review what BOSCO detected before generating resources.
      </p>
    </div>
  );
}

function KitPickerMock({ items, selections, onToggle }) {
  return (
    <div>
      <div className="grid grid-cols-2 gap-2 max-h-[280px] overflow-y-auto pr-1" data-testid="experience-bosco-kit">
        {items.map((it) => {
          const checked = !!selections[it.id];
          return (
            <button
              type="button"
              key={it.id}
              onClick={() => onToggle(it.id)}
              className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-xs transition-colors
                ${checked
                  ? 'border-mk-blue bg-mk-ice dark:bg-mk-midnight/40 text-mk-ink dark:text-slate-50'
                  : 'border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy text-mk-muted dark:text-slate-400 hover:border-mk-cyan dark:hover:border-mk-deep'}`}
              data-testid={`experience-bosco-kit-item-${it.id}`}
              aria-pressed={checked}
            >
              <span className={`h-4 w-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                checked
                  ? 'border-mk-blue bg-mk-blue text-white'
                  : 'border-slate-300 dark:border-slate-600 bg-transparent'
              }`}>
                {checked && <Check className="h-2.5 w-2.5" strokeWidth={3.5} />}
              </span>
              <span className="truncate">{it.label}</span>
            </button>
          );
        })}
      </div>
      <p className="mt-3 text-[11px] text-slate-500">
        Tap items to preview the toggle experience — the real picker offers the same one-tap selection.
      </p>
    </div>
  );
}

function GenerateList({ items, revealed, finaleLabel }) {
  const allReady = revealed >= items.length;
  return (
    <div data-testid="experience-bosco-generate">
      <ul className="space-y-2">
        {items.map((it, i) => {
          const ready = i < revealed;
          return (
            <li
              key={it.label}
              className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm transition-all
                ${ready
                  ? 'border-mk-blue/50 bg-mk-ice/70 dark:bg-mk-midnight/30 text-mk-ink dark:text-slate-50'
                  : 'border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy text-slate-500'}`}
            >
              <span className="inline-flex items-center gap-2">
                {ready ? (
                  <CheckCircle2 className="h-4 w-4 text-mk-blue" />
                ) : (
                  <span className="h-4 w-4 rounded-full border-2 border-slate-300 dark:border-slate-700 border-t-mk-blue animate-spin" aria-hidden="true" />
                )}
                {it.label}
              </span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                {ready ? 'ready' : 'building'}
              </span>
            </li>
          );
        })}
      </ul>
      {allReady && (
        <div className="mt-3 rounded-lg bg-mk-blue text-white text-xs font-semibold px-3 py-2 text-center">
          {finaleLabel || 'Generation Complete'}
        </div>
      )}
    </div>
  );
}

function DoneCard() {
  return (
    <div className="rounded-2xl border border-mk-cyan/60 dark:border-mk-edge bg-gradient-to-br from-mk-ice to-white dark:from-mk-midnight/40 dark:to-slate-900 p-6" data-testid="experience-bosco-done">
      <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-mk-ice dark:bg-mk-navy/40 text-mk-deep dark:text-mk-cyan text-[10px] font-bold uppercase tracking-[0.2em]">
        <CheckCircle2 className="h-3 w-3" /> Complete
      </div>
      <h3 className="mt-3 text-xl sm:text-2xl font-bold tracking-tight text-mk-ink dark:text-slate-50">
        From one textbook page to an entire classroom teaching kit.
      </h3>
      <p className="mt-2 text-sm text-mk-muted dark:text-slate-300 leading-relaxed">
        BOSCO understands educational content and transforms it into curriculum-ready teaching resources.
      </p>

      {/* Resource chips — quick summary of what was produced */}
      <div className="mt-4 flex flex-wrap gap-1.5">
        {['Lesson Plan', 'PPT', 'Worksheet', 'HOTS', 'Rubrics', 'Assessment'].map((r) => (
          <span key={r} className="inline-flex items-center gap-1 rounded-full bg-white dark:bg-mk-navy border border-mk-cyan/70 dark:border-mk-edge text-mk-deep dark:text-mk-cyan text-[10px] font-semibold px-2 py-0.5">
            <CheckCircle2 className="h-3 w-3" /> {r}
          </span>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Link to="/signup?plan=elite">
          <Button
            size="lg"
            className="bg-mk-blue hover:bg-mk-deep text-white font-semibold shadow shadow-mk-blue/30 h-11 px-6"
            data-testid="experience-bosco-cta"
          >
            Start Creating with BOSCO <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </Link>
        <span className="text-[11px] text-slate-500">Available exclusively with Elite subscription</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Progress rail — 7 dots below the caption panel
// ─────────────────────────────────────────────────────────────────────
function ProgressRail({ current, total }) {
  return (
    <div className="flex items-center gap-1.5 flex-1" role="progressbar"
         aria-valuemin={0} aria-valuemax={total - 1} aria-valuenow={current}
         aria-label="Demo progress"
         data-testid="experience-bosco-progress">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={`h-1.5 rounded-full transition-all duration-300 ${
            i < current
              ? 'w-4 bg-mk-blue'
              : i === current
                ? 'w-6 bg-mk-blue'
                : 'w-3 bg-slate-200 dark:bg-slate-800'
          }`}
        />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Intelligence rail — always on, reinforces the "understanding" story.
// ─────────────────────────────────────────────────────────────────────
function IntelligenceRail() {
  return (
    <div className="mt-10 pt-8 border-t border-mk-line dark:border-mk-edge"
         data-testid="experience-bosco-intelligence-rail">
      <div className="text-center mb-5">
        <div className="inline-flex items-center gap-1.5 text-mk-deep dark:text-mk-cyan text-[10px] uppercase font-bold tracking-[0.22em]">
          <Sparkles className="h-3 w-3" /> Educational Intelligence
        </div>
        <h3 className="mt-2 text-lg sm:text-xl font-bold tracking-tight text-mk-ink dark:text-slate-50">
          BOSCO understands more than printed text.
        </h3>
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        {INTELLIGENCE_PILLARS.map((p) => (
          <span
            key={p}
            className="inline-flex items-center gap-1.5 rounded-full border border-mk-cyan/60 dark:border-mk-edge bg-mk-ice dark:bg-mk-midnight/40 text-mk-deep dark:text-mk-cyan text-[11px] font-semibold px-2.5 py-1"
          >
            <CheckCircle2 className="h-3 w-3" /> {p}
          </span>
        ))}
      </div>
    </div>
  );
}
